import React, { ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { adminPermission, searchPermission } from '../constants/userPermissions';
import useIsMobileDevice from '../hooks/useIsMobileDevice';
import MobileBottomNav from './MobileBottomNav';

type MobileDesktopOnlyNoticeProps = {
  pageName: string;
};

// Full-screen notice for pages that only have a desktop layout. Rendering
// the raw desktop page (permanent sidebar, wide grids) at 390px is worse
// than saying so honestly — and for charts-only users this is their landing
// page, so it must carry the bottom nav to keep the rest of the app usable.
const MobileDesktopOnlyNotice = ({ pageName }: MobileDesktopOnlyNoticeProps) => {
  const navigate = useNavigate();
  const permissions = useSelector(
    (store: RootState) => store.user.user?.permissions?.map((perm) => perm.action) ?? [],
  );
  const canSearch = permissions.includes(searchPermission)
    || permissions.includes(adminPermission);

  return (
    <Box
      display="flex"
      flexDirection="column"
      bgcolor="background.default"
      sx={{
        height: '100vh',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        '@supports (height: 100dvh)': {
          height: '100dvh',
        },
        paddingTop: 'max(env(safe-area-inset-top, 0px), 44px)',
      }}
    >
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        px={4}
        textAlign="center"
        gap={1.5}
      >
        <Typography fontWeight={600}>
          {pageName} is designed for desktop
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
        >
          Open VIPR on a computer to use {pageName}.
        </Typography>
        {canSearch && (
          <Button
            variant="contained"
            onClick={() => navigate('/dashboard')}
            sx={{ textTransform: 'none', borderRadius: '10px', mt: 1 }}
          >
            Go to Dashboard
          </Button>
        )}
      </Box>
      <MobileBottomNav />
    </Box>
  );
};

// Route-level wrapper: renders the notice on phones, the page on desktop.
export const DesktopOnly = ({ pageName, children }: { pageName: string; children: ReactNode }) => {
  const isMobileDevice = useIsMobileDevice();
  if (isMobileDevice) return <MobileDesktopOnlyNotice pageName={pageName} />;
  return <>{children}</>;
};

export default MobileDesktopOnlyNotice;
